import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  CreditCard,
  TrendingUp,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  Loader,
  AlertCircle
} from 'lucide-react';
import { userAPI, transactionAPI } from '../utils/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalTransactions: 0,
    totalVolume: 0,
    pendingTransactions: 0,
    successRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [recentTransactions, setRecentTransactions] = useState([]);

  // Fetch dashboard data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch data in parallel
      const [userStatsRes, transactionStatsRes, recentTransRes] = await Promise.all([
        userAPI.getUserStats(),
        transactionAPI.getTransactionStats(),
        transactionAPI.getTransactions({ limit: 5, page: 1, sort: '-createdAt' })
      ]);

      // Process user stats
      if (userStatsRes.data.success) {
        const userStatsData = userStatsRes.data.data;
        setStats(prev => ({
          ...prev,
          totalUsers: userStatsData.users?.total || 0,
          activeUsers: userStatsData.users?.active || 0
        }));
      }

      // Process transaction stats
      if (transactionStatsRes.data.success) {
        const txStatsData = transactionStatsRes.data.data;
        setStats(prev => ({
          ...prev,
          totalTransactions: txStatsData.counts?.total || 0,
          totalVolume: txStatsData.volume?.total || 0,
          pendingTransactions: txStatsData.counts?.pending || 0,
          successRate: parseFloat(txStatsData.counts?.success_rate || 0)
        }));
      }

      // Process recent transactions
      if (recentTransRes.data.success) {
        setRecentTransactions(recentTransRes.data.data.docs || []);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'vetme-dark' }) => (
    <div className="card-vetme p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-vetme-gray-dark">{title}</p>
          <p className={`text-3xl font-bold text-${color} mt-2`}>{value}</p>
          {subtitle && (
            <p className="text-sm text-vetme-gray-dark mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`w-12 h-12 bg-vetme-mint-very-pale rounded-lg flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-vetme-mint-dark`} />
        </div>
      </div>
      
      {trend && (
        <div className="flex items-center mt-4 pt-4 border-t border-vetme-gray-light">
          {trend === 'up' ? (
            <ArrowUpRight className="w-4 h-4 text-green-500 mr-1" />
          ) : (
            <ArrowDownRight className="w-4 h-4 text-red-500 mr-1" />
          )}
          <span className={`text-sm font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trendValue}
          </span>
          <span className="text-sm text-vetme-gray-dark ml-1">vs last month</span>
        </div>
      )}
    </div>
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="badge-success">Completed</span>;
      case 'pending':
        return <span className="badge-warning">Pending</span>;
      case 'failed':
        return <span className="badge-error">Failed</span>;
      default:
        return <span className="badge-info">Unknown</span>;
    }
  };

  const formatCurrency = (amount, currency = 'NGN') => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Extract timestamp from MongoDB ObjectId
  const getTimestampFromId = (id) => {
    if (!id) return null;
    try {
      // MongoDB ObjectId first 8 characters represent timestamp in seconds
      const timestamp = parseInt(id.substring(0, 8), 16) * 1000;
      return new Date(timestamp);
    } catch (error) {
      return null;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="w-12 h-12 text-vetme-mint-dark animate-spin mx-auto" />
          <p className="mt-4 text-vetme-gray-dark">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-red-800 font-medium">Error Loading Dashboard</h3>
              <p className="text-red-600 mt-1">{error}</p>
              <button
                onClick={fetchDashboardData}
                className="mt-4 btn-vetme-primary"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="vetme-heading-big text-vetme-dark">Dashboard</h1>
          <p className="mt-2 text-vetme-gray-dark">
            Welcome back! Here's what's happening with VetmePay today.
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={stats.totalUsers.toLocaleString()}
          subtitle={`${stats.activeUsers} active`}
          icon={Users}
          trend="up"
          trendValue="+12%"
        />
        <StatCard
          title="Total Transactions"
          value={stats.totalTransactions.toLocaleString()}
          subtitle={`${stats.pendingTransactions} pending`}
          icon={CreditCard}
          trend="up"
          trendValue="+8%"
        />
        <StatCard
          title="Total Volume"
          value={formatCurrency(stats.totalVolume)}
          subtitle="This month"
          icon={DollarSign}
          trend="up"
          trendValue="+15%"
        />
        <StatCard
          title="Success Rate"
          value={`${stats.successRate}%`}
          subtitle="Last 30 days"
          icon={TrendingUp}
          trend="up"
          trendValue="+2%"
        />
      </div>

      {/* Recent Transactions */}
      <div className="card-vetme">
        <div className="px-6 py-4 border-b border-vetme-gray-light">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-vetme-dark">Recent Transactions</h3>
            <Link to="/transactions" className="btn-vetme-outline inline-flex items-center">
              <Eye className="w-4 h-4 mr-2" />
              View All
            </Link>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-vetme-gray-lightest">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Transaction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Time
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-vetme-gray-light">
              {recentTransactions.length > 0 ? (
                recentTransactions.map((transaction) => (
                  <tr key={transaction._id || transaction.id} className="hover:bg-vetme-gray-lightest">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-vetme-dark">
                        {transaction.reference || transaction._id || 'N/A'}
                      </div>
                      <div className="text-sm text-vetme-gray-dark">
                        {transaction.crypto_currency || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-vetme-dark">
                        {transaction.user?.first_name || 'User'} {transaction.user?.last_name || ''}
                      </div>
                      <div className="text-sm text-vetme-gray-dark">
                        {transaction.user?.phone || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        transaction.ramp_type === 'onramp'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {transaction.ramp_type === 'onramp' ? 'Buy Crypto' : 'Sell Crypto'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-vetme-dark">
                      {formatCurrency(transaction.fiat_amount || transaction.amount || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(transaction.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-vetme-gray-dark">
                      {formatTime(
                        transaction.createdAt ||
                        transaction.timestamp ||
                        getTimestampFromId(transaction.id || transaction._id)
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-vetme-gray-dark">
                    No recent transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;